// Iter158 · CRM ligero de reuniones (cliente/interna/técnica/seguimiento)
// asociadas a un proyecto. Sustituye el tracking suelto en Excel: incluye
// asistentes, temas, decisiones y action items con owner/deadline/estado.
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, X, Trash2, Pencil, Users, CheckCircle2, Clock, AlertTriangle,
  Calendar, Loader2, ChevronDown, ChevronRight, Upload,
} from "lucide-react";
import { API } from "@/context/AuthContext";

const TIPO_META = {
  cliente:     { label: "Cliente",     cls: "bg-blue-100 text-blue-800 border-blue-300" },
  interna:     { label: "Interna",     cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  tecnica:     { label: "Técnica",     cls: "bg-violet-100 text-violet-800 border-violet-300" },
  seguimiento: { label: "Seguimiento", cls: "bg-amber-100 text-amber-800 border-amber-300" },
};

const STATUS_META = {
  pendiente:   { label: "Pendiente",   cls: "bg-gray-100 text-gray-700",   Icon: Clock },
  en_progreso: { label: "En progreso", cls: "bg-blue-100 text-blue-800",   Icon: Loader2 },
  completado:  { label: "Completado",  cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  cancelado:   { label: "Cancelado",   cls: "bg-rose-100 text-rose-800",   Icon: X },
};

export default function ProjectMeetings({ projectId }) {
  const [data, setData] = useState({ items: [], kpis: {} });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/meetings`);
      setData(r.data);
    } catch (e) {
      toast.error("Error cargando reuniones");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (projectId) load(); }, [projectId]);

  const del = async (mid) => {
    if (!window.confirm("¿Eliminar reunión?")) return;
    await axios.delete(`${API}/projects/${projectId}/meetings/${mid}`);
    toast.success("Reunión eliminada");
    load();
  };

  const toggleActionStatus = async (mid, actionId, next) => {
    try {
      await axios.patch(`${API}/projects/${projectId}/meetings/${mid}/actions/${actionId}`, { status: next });
      load();
    } catch (e) {
      toast.error("No se pudo actualizar la acción");
    }
  };

  // Iter159 · Importa Excel de seguimiento (formato Grúa ZGZ)
  const importExcel = async (file) => {
    if (!file) return;
    setImporting(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data: r } = await axios.post(`${API}/projects/${projectId}/meetings/import-excel`, form);
      toast.success(`Importado: ${r.meetings_imported} reuniones · ${r.actions_imported} action items${r.skipped ? ` · ${r.skipped} omitidas (duplicadas)` : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error importando Excel");
    } finally { setImporting(false); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="p-5 space-y-4" data-testid="project-meetings">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 font-['Outfit']">
            Reuniones <span className="text-gray-400 font-normal">({data.items.length})</span>
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Cliente, internas, técnicas · con action items y seguimiento
          </p>
        </div>
        <div className="flex items-center gap-3">
          {data.kpis?.open_actions > 0 && (
            <div className="text-xs text-gray-600 flex items-center gap-1" data-testid="meetings-kpi-open">
              <Clock size={12}/> <b>{data.kpis.open_actions}</b> acciones abiertas
            </div>
          )}
          {data.kpis?.overdue_actions > 0 && (
            <div className="text-xs text-rose-700 flex items-center gap-1 font-semibold" data-testid="meetings-kpi-overdue">
              <AlertTriangle size={12}/> <b>{data.kpis.overdue_actions}</b> vencidas
            </div>
          )}
          <button
            onClick={() => setEditing({})}
            className="flex items-center gap-2 bg-[#007934] text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-[#006528]"
            data-testid="add-meeting-btn">
            <Plus size={14}/> Nueva reunión
          </button>
          <label className={`flex items-center gap-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-lg text-sm font-medium cursor-pointer ${importing ? "opacity-50 pointer-events-none" : ""}`} data-testid="import-meetings-excel-btn">
            {importing ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
            Importar Excel
            <input type="file" accept=".xlsx,.xlsm" className="hidden" onChange={e => importExcel(e.target.files?.[0])}/>
          </label>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400 text-sm">Cargando…</div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-12 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
          <Calendar size={32} className="mx-auto mb-2 opacity-30"/>
          <p className="text-sm">Sin reuniones. Añade la primera para empezar el seguimiento.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.items.map(m => {
            const meta = TIPO_META[m.type] || TIPO_META.seguimiento;
            const isOpen = expanded[m.id];
            const openActs = (m.action_items || []).filter(a => a.status !== "completado" && a.status !== "cancelado").length;
            const overdueActs = (m.action_items || []).filter(a => (a.status === "pendiente" || a.status === "en_progreso") && a.deadline && a.deadline < today).length;
            return (
              <div key={m.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`meeting-row-${m.id}`}>
                <div className="flex items-center gap-3 p-3">
                  <button onClick={() => setExpanded(e => ({ ...e, [m.id]: !e[m.id] }))} className="text-gray-400 hover:text-gray-700">
                    {isOpen ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                  </button>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>
                  <span className="text-xs font-mono text-gray-500 tabular-nums">{m.date}{m.time ? ` · ${m.time}` : ""}</span>
                  <span className="text-sm font-semibold text-gray-800 flex-1 truncate">{m.title || <span className="text-gray-400 italic">Sin título</span>}</span>
                  {m.client_name && <span className="text-[11px] text-gray-500 hidden md:inline">{m.client_name}</span>}
                  {(m.attendees || []).length > 0 && (
                    <span className="text-[11px] text-gray-500 flex items-center gap-1"><Users size={11}/>{m.attendees.length}</span>
                  )}
                  {openActs > 0 && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${overdueActs > 0 ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"}`} title={`${openActs} acción(es) abiertas${overdueActs > 0 ? `, ${overdueActs} vencidas` : ""}`}>
                      {overdueActs > 0 ? `${overdueActs}!/${openActs}` : openActs}
                    </span>
                  )}
                  <button onClick={() => setEditing(m)} className="text-indigo-600 hover:bg-indigo-50 p-1 rounded" data-testid={`meeting-edit-${m.id}`}><Pencil size={12}/></button>
                  <button onClick={() => del(m.id)} className="text-rose-500 hover:bg-rose-50 p-1 rounded" data-testid={`meeting-delete-${m.id}`}><Trash2 size={12}/></button>
                </div>
                {isOpen && (
                  <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-3 text-xs" data-testid={`meeting-detail-${m.id}`}>
                    {m.attendees && m.attendees.length > 0 && (
                      <div><span className="font-bold text-gray-500 uppercase text-[10px]">Asistentes:</span> {m.attendees.join(", ")}</div>
                    )}
                    {m.topics && (
                      <div><span className="font-bold text-gray-500 uppercase text-[10px]">Temas:</span> <span className="whitespace-pre-wrap">{m.topics}</span></div>
                    )}
                    {m.decisions && (
                      <div><span className="font-bold text-gray-500 uppercase text-[10px]">Decisiones:</span> <span className="whitespace-pre-wrap">{m.decisions}</span></div>
                    )}
                    {(m.action_items || []).length > 0 && (
                      <div>
                        <div className="font-bold text-gray-500 uppercase text-[10px] mb-1">Action items:</div>
                        <div className="space-y-1">
                          {m.action_items.map(a => {
                            const sm = STATUS_META[a.status] || STATUS_META.pendiente;
                            const isOverdue = (a.status === "pendiente" || a.status === "en_progreso") && a.deadline && a.deadline < today;
                            return (
                              <div key={a.id} className={`flex items-center gap-2 p-2 rounded border ${isOverdue ? "border-rose-300 bg-rose-50" : "border-gray-200 bg-white"}`} data-testid={`action-${a.id}`}>
                                <select value={a.status} onChange={e => toggleActionStatus(m.id, a.id, e.target.value)}
                                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded border-0 ${sm.cls}`}
                                        data-testid={`action-status-${a.id}`}>
                                  {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                </select>
                                <span className={`flex-1 ${a.status === "completado" || a.status === "cancelado" ? "line-through text-gray-400" : ""}`}>{a.title}</span>
                                {a.owner && <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{a.owner}</span>}
                                {a.deadline && (
                                  <span className={`text-[10px] font-mono ${isOverdue ? "text-rose-700 font-bold" : "text-gray-500"}`}>{a.deadline}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {m.notes && (
                      <div><span className="font-bold text-gray-500 uppercase text-[10px]">Notas:</span> <span className="whitespace-pre-wrap text-gray-600">{m.notes}</span></div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing !== null && (
        <MeetingModal
          projectId={projectId}
          meeting={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function MeetingModal({ projectId, meeting, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    date: meeting?.date || new Date().toISOString().slice(0, 10),
    time: meeting?.time || "",
    type: meeting?.type || "seguimiento",
    title: meeting?.title || "",
    client_name: meeting?.client_name || "",
    attendees: (meeting?.attendees || []).join(", "),
    topics: meeting?.topics || "",
    decisions: meeting?.decisions || "",
    action_items: meeting?.action_items || [],
    notes: meeting?.notes || "",
  }));
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setF(p => ({ ...p, [k]: v }));

  const addAction = () => update("action_items", [...f.action_items, {
    id: crypto.randomUUID(), title: "", owner: "", deadline: "", status: "pendiente", notes: "",
  }]);
  const updateAction = (idx, k, v) => {
    const copy = [...f.action_items];
    copy[idx] = { ...copy[idx], [k]: v };
    update("action_items", copy);
  };
  const rmAction = (idx) => update("action_items", f.action_items.filter((_, i) => i !== idx));

  const save = async () => {
    if (!f.date) { toast.error("Fecha obligatoria"); return; }
    if (!f.title.trim()) { toast.error("Título obligatorio"); return; }
    setSaving(true);
    const payload = {
      ...f,
      attendees: f.attendees.split(",").map(s => s.trim()).filter(Boolean),
      action_items: f.action_items.filter(a => a.title.trim()),
    };
    try {
      if (meeting?.id) {
        await axios.put(`${API}/projects/${projectId}/meetings/${meeting.id}`, payload);
        toast.success("Reunión actualizada");
      } else {
        await axios.post(`${API}/projects/${projectId}/meetings`, payload);
        toast.success("Reunión creada");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando reunión");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="meeting-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">{meeting?.id ? "Editar reunión" : "Nueva reunión"}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={18}/></button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-3 text-xs">
          <div className="grid grid-cols-4 gap-3">
            <L label="Fecha *"><input type="date" value={f.date} onChange={e=>update("date", e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1.5" data-testid="meeting-date"/></L>
            <L label="Hora"><input type="time" value={f.time} onChange={e=>update("time", e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1.5" data-testid="meeting-time"/></L>
            <L label="Tipo *">
              <select value={f.type} onChange={e=>update("type", e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1.5 bg-white" data-testid="meeting-type">
                {Object.entries(TIPO_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </L>
            <L label="Cliente"><input value={f.client_name} onChange={e=>update("client_name", e.target.value)} placeholder="Sólo si es tipo cliente" className="w-full border border-gray-200 rounded px-2 py-1.5" data-testid="meeting-client"/></L>
          </div>
          <L label="Título *"><input value={f.title} onChange={e=>update("title", e.target.value)} placeholder="Ej: Seguimiento semanal Grúa ZGZ" className="w-full border border-gray-200 rounded px-2 py-1.5" data-testid="meeting-title"/></L>
          <L label="Asistentes (separados por coma)"><input value={f.attendees} onChange={e=>update("attendees", e.target.value)} placeholder="dredondo@valorizasm.com, cliente@ayto.zgz.es" className="w-full border border-gray-200 rounded px-2 py-1.5" data-testid="meeting-attendees"/></L>
          <L label="Temas tratados"><textarea value={f.topics} onChange={e=>update("topics", e.target.value)} rows={3} className="w-full border border-gray-200 rounded px-2 py-1.5" data-testid="meeting-topics"/></L>
          <L label="Decisiones"><textarea value={f.decisions} onChange={e=>update("decisions", e.target.value)} rows={2} className="w-full border border-gray-200 rounded px-2 py-1.5" data-testid="meeting-decisions"/></L>

          {/* Action items */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] font-bold uppercase text-gray-500 tracking-wide">Action items ({f.action_items.length})</label>
              <button type="button" onClick={addAction} className="text-[11px] text-indigo-600 hover:underline inline-flex items-center gap-1" data-testid="meeting-add-action">
                <Plus size={11}/> Añadir acción
              </button>
            </div>
            <div className="space-y-2">
              {f.action_items.map((a, idx) => (
                <div key={a.id || idx} className="border border-gray-200 rounded p-2 bg-gray-50" data-testid={`meeting-action-${idx}`}>
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <input value={a.title} onChange={e=>updateAction(idx, "title", e.target.value)} placeholder="Tarea…" className="col-span-5 border border-gray-200 rounded px-2 py-1 bg-white" data-testid={`meeting-action-title-${idx}`}/>
                    <input value={a.owner} onChange={e=>updateAction(idx, "owner", e.target.value)} placeholder="Owner" className="col-span-3 border border-gray-200 rounded px-2 py-1 bg-white"/>
                    <input type="date" value={a.deadline || ""} onChange={e=>updateAction(idx, "deadline", e.target.value)} className="col-span-2 border border-gray-200 rounded px-2 py-1 bg-white"/>
                    <select value={a.status} onChange={e=>updateAction(idx, "status", e.target.value)} className="col-span-1 border border-gray-200 rounded px-1 py-1 bg-white text-[10px]">
                      {Object.entries(STATUS_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                    <button type="button" onClick={() => rmAction(idx)} className="col-span-1 text-rose-500 hover:bg-rose-50 rounded p-1 justify-self-end"><Trash2 size={11}/></button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <L label="Notas libres"><textarea value={f.notes} onChange={e=>update("notes", e.target.value)} rows={2} className="w-full border border-gray-200 rounded px-2 py-1.5" data-testid="meeting-notes"/></L>
        </div>

        <div className="p-3 border-t border-gray-100 bg-gray-50 flex justify-end gap-2 rounded-b-2xl">
          <button onClick={onClose} className="text-xs border border-gray-300 px-3 py-1.5 rounded hover:bg-gray-100">Cancelar</button>
          <button onClick={save} disabled={saving} className="text-xs bg-[#007934] hover:bg-[#006528] text-white px-3 py-1.5 rounded font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="meeting-save">
            {saving ? <Loader2 size={11} className="animate-spin"/> : <CheckCircle2 size={11}/>}
            {meeting?.id ? "Guardar cambios" : "Crear reunión"}
          </button>
        </div>
      </div>
    </div>
  );
}

function L({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase text-gray-500 tracking-wide mb-1">{label}</label>
      {children}
    </div>
  );
}
